
const {
  ONE_HOUR,
  ONE_DAY,
  ONE_WEEK,
} = require('./timeConstants')


let globalFirestore
let globalLogger
let globalIsInitialized = false


exports.firebaseId = process.env.GCLOUD_PROJECT // functions
  || process.env.VUE_APP_GCLOUD_PROJECT // website
  || process.env.FIREBASE_PROJECT_ID // mobile
  || process.env.VUE_APP_FIREBASE_PROJECT_ID // chrome extension
exports.isDev = exports.firebaseId === 'happs-tv-dev'

/**
 * Initialize the Shared library by passing in a reference to Firestore
 *
 * @param {Firestore} firestore Reference to firebase.firestore
 * @param {Pino} logger An initialized Pino logger
 */
exports.initShared = function (firestore, logger = undefined) {
  globalFirestore = firestore
  globalLogger = (logger) ? logger : console
  globalIsInitialized = true
}

exports.firestoreBase = function() {
  if (!globalIsInitialized) {
    throw new Error('Shared is not initialized. Please call initShared(firestore) first.')
  }
  return globalFirestore
}

exports.firestore = function() {
  return exports.firestoreBase()()
}

exports.isFirebaseAdminSDK = function() {
  // app key doesn't exist in the admin sdk
  return !(exports.firestore() || {}).app
}

exports.loggerBase = function () {
  return globalLogger
}


exports.date = function(object){
  if (object === undefined || object === null) {
    return undefined
  }

  switch (typeof object) {
    case 'object':
      if (object instanceof Date) {
        return object
      }

      if (globalFirestore?.Timestamp && (object instanceof globalFirestore?.Timestamp)) {
        return object.toDate()
      }

      // in case Timestamp object has been de-classed
      if (object.seconds !== undefined && object.nanoseconds !== undefined) {
        return new Date(Math.round(object.seconds * 1000 + object.nanoseconds / 1000000))
      }
      /* eslint-disable no-underscore-dangle */
      if (object._seconds !== undefined && object._nanoseconds !== undefined) {
        return new Date(Math.round(object._seconds * 1000 + object._nanoseconds / 1000000))
      }
      /* eslint-enable no-underscore-dangle */
      break
    case 'number':
      return new Date(object)
    default:
      break
  }
  return undefined
}

exports.timestamp = function(object){
  if (object === undefined) {
    return undefined
  }

  switch (typeof object) {
    case 'object':
      if (object instanceof Date) {
        return globalFirestore.Timestamp.fromDate(object)
      }

      if (object instanceof globalFirestore.Timestamp) {
        return object
      }

      // in case Timestamp object has been de-classed
      if (object.seconds !== undefined && object.nanoseconds !== undefined) {
        return new globalFirestore.Timestamp(object.seconds, object.nanoseconds)
      }
      /* eslint-disable no-underscore-dangle */
      if (object._seconds !== undefined && object._nanoseconds !== undefined) {
        return new globalFirestore.Timestamp(object._seconds, object._nanoseconds)
      }
      /* eslint-enable no-underscore-dangle */
      break
    case 'number':
      return exports.timestamp(new Date(object))
    default:
      break
  }

  return undefined
}

// TODO Ensure the firestoreUtils versions of these functions are referenced
// in `functions` and `services`.
// exports.cloudIncrement = globalFirestore.FieldValue.increment
// exports.cloudTimestamp = globalFirestore.FieldValue.serverTimestamp()


exports.uniformLength = function(length, of, leftFiller) {
  if(of === undefined){
    return ''
  }
    const string = (typeof of === 'string') ? of : of.toString()
    let zeroCount = (length - string.length) > 0 ? (length - string.length) : 0
    return (leftFiller || "0").repeat(zeroCount) + string.slice(string.length > length ? (string.length - length) : 0)
}


exports.durationString = function(seconds) {
  const mins = Math.floor(seconds / 60)
  const hrs = Math.floor(mins / 60)
  return `${hrs ? `${hrs}:` : ''}${exports.uniformLength(2, mins % 60)}:${exports.uniformLength(2, Math.floor(seconds) % 60)}`
}


exports.flattenArray = function(array, depth = 1) {
  return array.reduce((flat, toFlatten) => {
    return flat.concat((Array.isArray(toFlatten) && (depth>1)) ? toFlatten.flat(depth-1) : toFlatten)
  }, [])
}


exports.incrementKey = function(dictionary, key, increment) {
  dictionary[key] = (dictionary[key] || 0) + increment
}

// convert all numbers to firebase increment objects
// note: nested increments don't work in setDoc (and are unnecessary in updateDoc)
exports.turnAllNumbersToIncrements = function(dictionary){
  const result = { ...dictionary }

  Object.keys(result).forEach(key => {
    const value = result[key]
    if(value === undefined || value === 0 || isNaN(value)){
      delete result[key]
    } else if(typeof value === 'number'){
      result[key] = globalFirestore.FieldValue.increment(value)
    }
  })

  return result
}


// month number, since January 1st, 1970, in UTC-10 (HST)
exports.statMonthNumber = function(maybeDateOrTimestamp){
  const millis = (exports.date(maybeDateOrTimestamp) || new Date()).getTime()

  const adjustedDate = new Date(millis - 10 * ONE_HOUR)

   return (adjustedDate.getUTCFullYear() - 1970) * 12 + adjustedDate.getUTCMonth()
}

// midnight Hawaii Standard Time (HST) of the first day of the month
exports.statMonthStartDate = function(maybeMonthNumber){
  const monthNumber = maybeMonthNumber || exports.statMonthNumber()

  const year = Math.floor(monthNumber / 12) + 1970
  const month = monthNumber % 12
  return new Date(Date.UTC(year, month) + 10 * ONE_HOUR)
}

// HST is 10 hours behind UTC, and we want the rollover to be midnight, so we subtract 10 hours from UTC
// the first Monday of 1970 was the 5th day of 1970, so we subtract 4 days to align to Monday being the beginning of the week
const JAN_1_UTC_TO_JAN_5_HST_OFFSET = (-10 * ONE_HOUR) + (-4 * ONE_DAY)

// milliseconds since Monday January 5th, 1970 at midnight Hawaii Standard Time (HST)
function getMillisSinceJan5th1970HST(maybeDateOrTimestamp) {
  const millis = (exports.date(maybeDateOrTimestamp) || new Date()).getTime()
  return millis + JAN_1_UTC_TO_JAN_5_HST_OFFSET
}

// week number (Monday through Sunday) of the given date, since Monday January 5th, 1970 at midnight Hawaii Standard Time (HST)
exports.statWeekNumber = function(maybeDateOrTimestamp){
  const millisSinceJan5th1970HST = getMillisSinceJan5th1970HST(maybeDateOrTimestamp)
  return Math.floor(millisSinceJan5th1970HST / ONE_WEEK)
}

// Monday at midnight Hawaii Standard Time (HST) of the week of the given date
exports.statWeekStartDate = function(maybeWeekNumber){
  const weekNumber = maybeWeekNumber === undefined ? exports.statWeekNumber() : maybeWeekNumber
  const jan5HSTMillis = weekNumber * ONE_WEEK
  return new Date(jan5HSTMillis - JAN_1_UTC_TO_JAN_5_HST_OFFSET)
}

// day number (Monday through Sunday) of the given date, since Monday January 5th, 1970 at midnight Hawaii Standard Time (HST)
exports.statDayNumber = function(maybeDateOrTimestamp){
  const millisSinceJan5th1970HST = getMillisSinceJan5th1970HST(maybeDateOrTimestamp)
  return Math.floor(millisSinceJan5th1970HST / ONE_DAY)
}

// at midnight Hawaii Standard Time (HST) of the day
exports.statDayStartDate = function(maybeDayNumber){
  const dayNumber = maybeDayNumber === undefined ? exports.statDayNumber() : maybeDayNumber
  const jan5HSTMillis = dayNumber * ONE_DAY
  return new Date(jan5HSTMillis - JAN_1_UTC_TO_JAN_5_HST_OFFSET)
}

// hour number (Monday through Sunday) of the given date, since Monday January 5th, 1970 at midnight Hawaii Standard Time (HST)
exports.statHourNumber = function(maybeDateOrTimestamp){
  const millisSinceJan5th1970HST = getMillisSinceJan5th1970HST(maybeDateOrTimestamp)
  return Math.floor(millisSinceJan5th1970HST / ONE_HOUR)
}

// on the hour of the hour
exports.statHourStartDate = function(maybeHourNumber){
  const hourNumber = maybeHourNumber === undefined ? exports.statHourNumber() : maybeHourNumber
  const jan5HSTMillis = hourNumber * ONE_HOUR
  return new Date(jan5HSTMillis - JAN_1_UTC_TO_JAN_5_HST_OFFSET)
}


// Converts date (or current time) to YYYY-MM-DD string
exports.statLogDate = function(maybeDateOrTimestamp){
  return (exports.date(maybeDateOrTimestamp) || new Date()).toISOString().split('T')[0]
}

exports.doPromiseFunctionsInOrder = async function(promiseFunctionList, stopIterationOnThrow = true){
  const logger = exports.loggerBase()

  const promiseFunctionListCopy = [...promiseFunctionList]

  const results = []
  let error

  while (promiseFunctionListCopy.length) {
    const promiseFunction = promiseFunctionListCopy.shift()
    try {
      // eslint-disable-next-line no-await-in-loop
      results.push(await promiseFunction())
    } catch(e) {
      if (stopIterationOnThrow) throw e
      if (!error) error = e
    }
  }

  if (error) throw error

  return results
}


exports.makeFirestoreFriendly = function(value, insideArray){

  if(!value){
    return value
  }

  if(Array.isArray(value)){
    if(insideArray){
      return { array: exports.makeFirestoreFriendly(value) }
    }

    return value.map(innerValue => {
      return exports.makeFirestoreFriendly(innerValue, true)
    })
  }

  if(typeof value === 'object'){
    Object.keys(value).forEach(key => {
      value[key] = exports.makeFirestoreFriendly(value[key])
    })
  }

  return value
}

// Recursively removes undefined values and nested arrays from an object
exports.makeObjectFirestoreSafe = function(object, parentTree = []){
  if (typeof object !== 'object') return object
  const result = {}
  Object.keys(object).forEach((key) => {
    const value = object[key]
    if (value === undefined) return
    if (typeof value === 'function') return
    if (value !== null && typeof value === 'object' && !Object.values(exports.firestoreBase()).includes(value.constructor) /* pass through things like Timestamp, FieldValue */) {
      if (parentTree.includes(value)) return // circular references aren't allowed
      if (Array.isArray(value)) {
        result[key] = value.filter((arrayItem) => {
          if (arrayItem === undefined) return false
          if (typeof arrayItem === 'function') return false
          if (arrayItem !== null && typeof arrayItem === 'object' && parentTree.includes(arrayItem)) return false // circular references aren't allowed
          if (arrayItem !== null && typeof arrayItem === 'object' && Array.isArray(arrayItem)) return false // nested arrays aren't allowed
          return true
        }).map((arrayItem) => {
          if (arrayItem !== null && typeof arrayItem === 'object') return exports.makeObjectFirestoreSafe(arrayItem, [...parentTree, value, arrayItem])
          return arrayItem
        })
      } else {
        result[key] = exports.makeObjectFirestoreSafe(value, [...parentTree, value])
      }
    } else result[key] = value
  })
  return result
}

exports.timeComponentToTimePeriod = function(timeComponent){

  let timePeriod = undefined

  const lowercaseComponent = timeComponent.toLowerCase()
  const timeValue = parseFloat(lowercaseComponent.substring(0, lowercaseComponent.length - 1))
  const timeUnit = lowercaseComponent.substring(lowercaseComponent.length - 1, lowercaseComponent.length)

  if(timeValue && !isNaN(timeValue) && timeValue > 0){
    switch(timeUnit){
    case 'd':
      timePeriod = timeValue * 24 * 60 * 60 * 1000
      break
    case 'h':
      timePeriod = timeValue  * 60 * 60 * 1000
      break
    case 'm':
      timePeriod = timeValue * 60 * 1000
    }
  }

  return timePeriod
}


exports.timeStringToTimePeriod = function(timeString){

  let timeStringSplitComponents = timeString.split(/([0-9.]+)/)

  if (timeStringSplitComponents.length > 0 && timeStringSplitComponents[0] === ''){
    timeStringSplitComponents = timeStringSplitComponents.splice(1)
  }

  if(timeStringSplitComponents.length === 1 || (timeStringSplitComponents.length % 2) === 1){
    return undefined
  }

  let timePeriod = 0

  for(let i = 0; i < timeStringSplitComponents.length; i += 2){
    const timePeriodIncrement = exports.timeComponentToTimePeriod(timeStringSplitComponents[i] + timeStringSplitComponents[i+1])

    if(timePeriodIncrement === undefined){
      return undefined
    }

    timePeriod += timePeriodIncrement
  }

  return timePeriod
}

exports.timePeriodToAppropriateString = function(timePeriod){
  const minutes = Math.floor(timePeriod / 1000 / 60)

  if(minutes < 120){
    return `${minutes} minute${minutes === 1 ? '' : 's'}`
  }

  const hours = Math.floor(minutes / 60)

  if(hours < 48){
    return `${hours} hour${hours === 1 ? '' : 's'} ${exports.timePeriodToAppropriateString((minutes % 60) * 60 * 1000)}`
  }

  const days = Math.floor(hours / 24)

  return `${days} day${days === 1 ? '' : 's'} ${exports.timePeriodToAppropriateString((minutes % (60 * 24)) * 60 * 1000)}`
}


exports.waitPromise = function(interval, maybeFunction){
  return new Promise(resolve => {
    setTimeout(()=>{
      resolve({})
    }, interval)
  }).then(() => {
    if (maybeFunction) return maybeFunction()
    return {}
  })
}

exports.filterObjectKeys = function(object, keys){
  let result = {}
  for(let key of keys){
    if(object[key] !== undefined) result[key] = object[key]
  }
  return result
}

/**
 * Works exactly like `Object.assign`, except nested keys are treated individually
 * 
 * For example:
 * ```
 * const obj1 = {
 *   a: 'obj1_a',
 *   b: 'obj1_b',
 *   c: {
 *     c1: 'obj1_c1',
 *     c2: 'obj1_c2',
 *   },
 * }
 * 
 * const obj2 = {
 *   b: 'obj2_b',
 *   c: {
 *     c2: 'obj2_c2',
 *   },
 * }
 * 
 * 
 * // Object.assign overwrites entire `c` object
 * Object.assign(obj1, obj2) === {
 *   a: 'obj1_a',
 *   b: 'obj2_b',
 *   c: {
 *     c2: 'obj2_c2',
 *   },
 * }
 * 
 * // nestedObjectAssign does not
 * nestedObjectAssign(obj1, obj2) === {
 *   a: 'obj1_a',
 *   b: 'obj2_b',
 *   c: {
 *     c1: 'obj1_c1',
 *     c2: 'obj2_c2',
 *   },
 * }
 * ```
 *
 * @param {target} the object to which values are assigned
 * @param {...args} the object(s) from which values are assigned
 */

exports.nestedObjectAssignWithOptions = function(options, target, ...args) {
  args.forEach((arg) => {
    Object.keys(arg).forEach((key) => {
      const value = arg[key]

      if (value && typeof value === 'object' && value.constructor === Object) {
        if (!target[key] || typeof target[key] !== 'object' || target[key].constructor !== Object) target[key] = {}
        exports.nestedObjectAssignWithOptions(options, target[key], value)
      } else {
        if (options.sumNumbers && typeof value === 'number' && (target[key] === undefined || typeof target[key] === 'number')) {
          target[key] = (target[key] || 0) + value
          if (options.roundNumbersToDecimalPlace !== undefined) {
            target[key] = Number(target[key].toFixed(options.roundNumbersToDecimalPlace))
          }
        } else if (options.concatArrays && Array.isArray(value) && (target[key] === undefined || Array.isArray(target[key]))) {
          target[key] = [...(target[key] || []), ...value]
        } else {
          target[key] = value
        }
      }
    })
  })

  return target
}

exports.nestedObjectAssign = function(target, ...args) {
  return exports.nestedObjectAssignWithOptions({}, target, ...args)
}

exports.deepObjectCopy = function(value) {
  if (typeof value !== 'object' || value === null) {
    return value
  }

  if (Array.isArray(value)) {
    return value.map(exports.deepObjectCopy)
  }

  const result = {}
  Object.keys(value).forEach((key) => {
    result[key] = exports.deepObjectCopy(value[key])
  })
  return result
}

// The normal isDeepStrictEqual returns false if the keys are out of order.
// For example, isDeepStrictEqual({ a: 1, b: 2 }, { b: 2, a: 1 }) returns false.
// We usually don't care about the key order, so here's this:
const isDeepStrictEqualUnordered = function(item1, item2, verbose, deepLocation = []) {
  if (typeof item1 !== typeof item2) {
    // eslint-disable-next-line no-console
    if (verbose) console.log(`Value types are different at: ${deepLocation.join('.')}`)
    return false
  }

  switch (typeof item1) {
    case 'object': {
      const isNull1 = item1 === null
      const isNull2 = item2 === null
      if (isNull1 !== isNull2) {
        // eslint-disable-next-line no-console
        if (verbose) console.log(`Values are different at: ${deepLocation.join('.')}`)
        return false
      }
      if (isNull1) {
        break
      }

      const isArray1 = Array.isArray(item1)
      const isArray2 = Array.isArray(item2)
      if (isArray1 !== isArray2) {
        // eslint-disable-next-line no-console
        if (verbose) console.log(`Values are different at: ${deepLocation.join('.')}`)
        return false
      }
      if (isArray1) {
        if (item1.length !== item2.length) {
          // eslint-disable-next-line no-console
          if (verbose) console.log(`Array lengths are different at: ${deepLocation.join('.')}`)
          return false
        }
        if (item1.some((_, i) => !isDeepStrictEqualUnordered(item1[i], item2[i], verbose, [...deepLocation, i]))) return false
        break
      }

      const keyList1 = Object.keys(item1)
      const keyList2 = Object.keys(item2)

      const keyIn1Only = keyList1.find(key => !keyList2.includes(key))
      if (keyIn1Only) {
        // eslint-disable-next-line no-console
        if (verbose) console.log(`Key not found in object 2: ${deepLocation.join('.')}`)
        return false
      }

      const keyIn2Only = keyList2.find(key => !keyList1.includes(key))
      if (keyIn2Only) {
        // eslint-disable-next-line no-console
        if (verbose) console.log(`Key not found in object 1: ${deepLocation.join('.')}`)
        return false
      }

      if (keyList1.some((key) => {
        return !isDeepStrictEqualUnordered(item1[key], item2[key], verbose, [...deepLocation, key])
      })) return false
      break
    }
    default: {
      if (item1 !== item2) {
        // eslint-disable-next-line no-console
        if (verbose) console.log(`Values are different at: ${deepLocation.join('.')}`)
        return false
      }
      break
    }
  }

  return true
}
Object.assign(exports, { isDeepStrictEqualUnordered })


class DataComparison {
  constructor(oldValue, newValue) {
    Object.assign(this, { oldValue, newValue })
  }

  transform(transformer = (v => v)) {
    const { oldValue, newValue } = this
    return new DataComparison(transformer(oldValue), transformer(newValue))
  }

  get isEqual() {
    return isDeepStrictEqualUnordered(this.oldValue, this.newValue)
  }

  get isUnequal() {
    return !this.isEqual
  }

  get addedArrayValues() {
    const { oldValue, newValue } = this
    if(!Array.isArray(oldValue) || !Array.isArray(newValue)) throw Error('Both values must be arrays to use "addedArrayValues"')
    return newValue.filter(newItem => !oldValue.some(oldItem => isDeepStrictEqualUnordered(newItem, oldItem)))
  }

  get removedArrayValues() {
    const { oldValue, newValue } = this
    if(!Array.isArray(oldValue) || !Array.isArray(newValue)) throw Error('Both values must be arrays to use "addedArrayValues"')
    return oldValue.filter(oldItem => !newValue.some(newItem => isDeepStrictEqualUnordered(oldItem, newItem)))
  }

  get numericalDiff() {
    const { oldValue, newValue } = this
    if([newValue, oldValue].some(value => !['number', 'undefined'].includes(typeofWithNull(value)))) throw Error('Both values must be numbers to use "numericalDiff"')

    return (newValue || 0) - (oldValue || 0)
  }

  get objectNumericalDiff() {
    const { oldValue, newValue } = this
    if([newValue, oldValue].some(value => !['object', 'undefined'].includes(typeofWithNull(value)))) throw Error('Both values must be objects to use "objectNumericalDiff"')

    return getNumericalDiffBetweenObjects(oldValue || {}, newValue || {})
  }

  get changedKeyList() {
    const { oldValue, newValue } = this
    if([newValue, oldValue].some(value => !['object', 'undefined'].includes(typeofWithNull(value)))) throw Error('Both values must be objects to use "changedKeyList"')

    const keyList = [...Object.keys(newValue || {}), ...Object.keys(oldValue || {})]
      .filter((key, i, a) => a.indexOf(key) === i)

    return keyList.filter(key => this.transform(value => value?.[key]).isUnequal)
  }

  get flattenObjectKeys() {
    const { oldValue, newValue } = this
    if([newValue, oldValue].some(value => !['object', 'undefined'].includes(typeofWithNull(value)))) throw Error('Both values must be objects to use "flattenObjectKeys"')

    return this.transform(value => value || {}).transform(exports.flattenObjectKeys)
  }
}
Object.assign(exports, { DataComparison })


// converts from object.a.b.c format to object['a.b.c'] format
exports.flattenObjectKeys = function(object) {
  const result = {}
  Object.keys(object).forEach((key) => {
    const value = object[key]
    if (value && typeof value === 'object' && value.constructor === Object && Object.keys(value).length) {
      const flattenedValue = exports.flattenObjectKeys(value)
      Object.keys(flattenedValue).forEach((valueKey) => {
        result[`${key}.${valueKey}`] = flattenedValue[valueKey]
      })
    } else {
      result[key] = value
    }
  })
  return result
}

// converts from object['a.b.c'] format to object.a.b.c format
exports.unflattenObjectKeys = function(object) {
  const unflattenedObjectList = Object.keys(object)
    .map((key) => {
      const value = object[key]
      return key
        .split('.')
        .reverse()
        .reduce((prevResult, subKey) => ({ [subKey]: prevResult }), value)
    })
  return exports.nestedObjectAssign({}, ...unflattenedObjectList)
}


// because typeof null === 'object' for some reason
const typeofWithNull = function(value) {
  if (value === null) return 'null'
  return typeof value
}
Object.assign(exports, { typeofWithNull })


function getNumericalDiffBetweenObjects(object1, object2) {
  const keyList = [...Object.keys(object1), ...Object.keys(object2)]
    .filter((key, i, a) => a.indexOf(key) === i)

  const result = {}
  keyList.forEach((key) => {
    const value1 = object1[key]
    const value2 = object2[key]

    const typeList = [typeofWithNull(value1), typeofWithNull(value2)]
      .filter(type => type !== 'undefined')
      .filter((type, i, a) => a.indexOf(type) === i)

    if (typeList.length === 1) {
      switch (typeList[0]) {
        case 'object': {
          const diff = getNumericalDiffBetweenObjects(value1 || {}, value2 || {})
          if (Object.keys(diff).length) result[key] = diff
          break
        }
        case 'number': {
          const diff = (value2 || 0) - (value1 || 0)
          if (diff) result[key] = diff
          break
        }
      }
    }
  })

  return result
}
Object.assign(exports, { getNumericalDiffBetweenObjects })

// this deeply returns the NEW value if it was changed; for numerical changes, use getDeepNumericalChanges
exports.getDeepChangesBetweenObjects = function(object1, object2, deletedValue = '<deleted>') {
  const keyList = [...Object.keys(object1), ...Object.keys(object2)]
    .filter((key, i, a) => a.indexOf(key) === i)

  const result = {}
  keyList.forEach((key) => {
    const value1 = object1[key]
    const value2 = object2[key]

    const typeList = [typeofWithNull(value1), typeofWithNull(value2)]
      .filter((type, i, a) => a.indexOf(type) === i)

    if (typeList.length === 1 && typeList[0] === 'object') {
      const diff = exports.getDeepChangesBetweenObjects(value1, value2, deletedValue)
      if (Object.keys(diff).length) result[key] = diff
    } else if (value1 !== value2) {
      if (value2 === undefined) result[key] = deletedValue
      else result[key] = value2
    }
  })

  return result
}


function getDeepNonZeroNumbers(object) {
  const result = {}
  Object.keys(object).forEach((key) => {
    const value = object[key]
    switch (typeofWithNull(value)) {
      case 'object': {
        if (Array.isArray(value)) return
 
        const recursiveResult = getDeepNonZeroNumbers(value)
        if (!Object.keys(recursiveResult).length) return

        result[key] = recursiveResult
        break
      }
      case 'number':
        if (!value) break

        result[key] = value
        break
      default:
        break
    }
  })
  return result
}
Object.assign(exports, { getDeepNonZeroNumbers })


exports.shuffleArray = function(array) {
  let j
  for (let i = array.length - 1; i > 0; i -= 1) {
    j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
}

/**
 * Generate one time passcode
 * @param {number} len Code length, default 6
 * @returns {string} Digit code string
 */
exports.generateOTP = function(len = 6) {
  if (len < 1) {
    return ''
  }
  return [...Array(len)].map(() => Math.floor(Math.random() * 10)).join('')
}


exports.runPromiseWithTimeout = async function(timeout, promise, defaultResult = { success: false, error: 'Operation timed out' }) {
  const logger = exports.loggerBase()

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    let resolved = false
    setTimeout(() => {
      if (!resolved) {
        logger.error('UTILS::: Promise timed out.')
        logger.trace()
        resolve(defaultResult)
      }
    }, timeout)

    const result = await promise
    resolved = true
    resolve(result)
  })
}

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

exports.isValidEmail = function(value) {
  return value.length > 0 && EMAIL_REGEX.test(value)
}

const URL_REGEX = /^(https?:\/\/)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/

exports.isValidUrlOrDomain = function (value) {
  return value.length > 0 && URL_REGEX.test(value)
}

/**
 * Rounds numbers, such as dollar amounts, to two decimals. Inspiration is from https://stackoverflow.com/a/40800717/2695192
 * @param {number} 6.666666
 * @returns {number} 6.67
 */
exports.roundToTwoDecimals = function(number) {    
    return Number(`${Math.round(`${number}e+2`)}e-2`)
}

/*
* @param {number} amount - The amount you would like to format. Ex: 2
* @param {number} fractionDigits - How many digits to format the number to. Ex: 2 to 2.00
*/
exports.formatNumber = function(amount = 0, fractionDigits = 0) {
  return amount.toLocaleString('en-US', { maximumFractionDigits: fractionDigits, minimumFractionDigits: fractionDigits })
}

/**
 * Formats large numbers count
 * @param {number} 24000
 * @returns {string} 24k
 */
 exports.numberMagnitudeString = function(number) {
  if (number < 1000) return number.toFixed(0).toString()
  if (number < 1000000) {
    const numString = (number / 1000).toFixed(1).toString()
    return `${(numString.slice(-2) === '.0' ? numString.slice(0, -2) : numString)}k`
  }
  const numString = (number / 1000000).toFixed(1).toString()
  return `${(numString.slice(-2) === '.0' ? numString.slice(0, -2) : numString)}m`
}


exports.validateSchema = function(item, schema, throwErrors, debugLocation = []) {
  if (!throwErrors) {
    try {
      return exports.validateSchema(item, schema, true, debugLocation)
    } catch(error) {
      return {
        success: false,
        error: error.message,
      }
    }
  }

  const [unrecognizedSchemaKey] = Object.keys(schema).filter(key => !['_keyIn', '_optional', '_type', '_schemaByKey', '_minLength', '_elementSchema', '_in'].includes(key))
  if (unrecognizedSchemaKey) {
    throw new Error(`[${debugLocation.join('.')}] Unrecognized schema key "${unrecognizedSchemaKey}".`)
  }

  const {
    _optional,
    _type,
    _schemaByKey,
    _minLength,
    _elementSchema,
    _in,
  } = schema

  if (item === undefined) {
    if (!_optional) {
      throw new Error(`[${debugLocation.join('.')}] Required field is missing.`)
    }
  } else {
    let itemType = typeof item
    if (item === null) itemType = 'null'
    else if (itemType === 'object' && Array.isArray(item)) itemType = 'array'

    if (_type) {
      const _typeArray = Array.isArray(_type) ? _type : [_type]
      if (!_typeArray.includes(itemType)) {
        throw new Error(`[${debugLocation.join('.')}] Expected type "${_type}", but detected type "${itemType}".`)
      }
    }

    if (_in) {
      if (!_in.includes(item)) {
        throw new Error(`[${debugLocation.join('.')}] Expected value to be within the set [${_in.map(_inValue => `"${_inValue}"`).join(', ')}], but detected value "${item}".`)
      }
    }

    if (itemType === 'object') {
      if (_schemaByKey) {
        Object.keys(_schemaByKey).filter(key => key !== '*').forEach((key) => {
          exports.validateSchema(item[key], _schemaByKey[key], true, [...debugLocation, key])
        })

        const unknownKeyList = Object.keys(item).filter(key => !_schemaByKey[key])
        if (unknownKeyList.length) {
          const wildcardSchema = _schemaByKey['*']
          if (!wildcardSchema) {
            throw new Error(`[${debugLocation.join('.')}] Unknown field "${unknownKeyList[0]}" detected.`)
          }
          if (wildcardSchema._keyIn) {
            const [wildcardUnknownKey] = unknownKeyList.filter(key => !wildcardSchema._keyIn.includes(key))
            if (wildcardUnknownKey) {
              throw new Error(`[${debugLocation.join('.')}] Unknown wildcard field "${wildcardUnknownKey}" detected.`)
            }
          }
          unknownKeyList.forEach((key) => {
            exports.validateSchema(item[key], wildcardSchema, true, [...debugLocation, key])
          })
        }
      }
    }

    if (itemType === 'array') {
      if (_minLength !== undefined) {
        if (item.length < _minLength) {
          throw new Error(`[${debugLocation.join('.')}] Expected array to have at least ${_minLength} elements, but detected ${item.length} elements.`)
        }
      }

      if (_elementSchema) {
        item.forEach((element, i) => {
          exports.validateSchema(element, _elementSchema, true, [...debugLocation, i])
        })
      }
    }
  }

  return { success: true }
}

exports.validateObjectSchema = function(object, _schemaByKey, throwErrors) {
  return exports.validateSchema(object, { _type: 'object', _schemaByKey }, throwErrors)
}

const TEMPLATE_REGEX = /\{\{(%)?(\w*)(?:\|([^|}]*))?\}\}/g
const ENCODED_TEMPLATE_REGEX = /%7B%7B(%25)?(\w*)(?:\|([^|]*))?%7D%7D/g

const getTemplateValue = (templateReplacementObject, templateKey, urlEncodeChar, defaultValue) => {
  const templateValue = templateReplacementObject[templateKey]
  if (templateValue) {
    if (urlEncodeChar) return encodeURIComponent(templateValue)
    return templateValue
  }
  if (defaultValue) {
    if (urlEncodeChar) return encodeURIComponent(defaultValue)
    return defaultValue
  }
  throw new Error(`Failed to generate template replacement for {{${templateKey}}}.`)
}

exports.replaceAllTemplateItemsInString = function(templateReplacementObject, rawValue, templatesAreEncoded) {
  const selectedRegex = templatesAreEncoded ? ENCODED_TEMPLATE_REGEX : TEMPLATE_REGEX
  return rawValue.replace(selectedRegex, (_, urlEncodeChar, templateKey, defaultValue) => {
    const result = getTemplateValue(templateReplacementObject, templateKey, urlEncodeChar, defaultValue)
    return templatesAreEncoded ? encodeURIComponent(result) : result
  })
}
